import { cn } from '@/lib/utils'

export interface BarItem {
  label: string
  value: number
  total?: number
  color?: string
  icon?: string
}

interface HorizontalBarChartProps {
  items: BarItem[]
  showPercent?: boolean
  className?: string
}

export function HorizontalBarChart({ items, showPercent = true, className }: HorizontalBarChartProps) {
  const total = items.reduce((acc, i) => acc + i.value, 0)
  return (
    <div className={cn('space-y-3', className)}>
      {items.map((it, i) => {
        const pct = it.total ? (it.value / it.total) * 100 : (it.value / total) * 100
        return (
          <div key={i}>
            <div className="flex justify-between text-[11px] text-slate-600 mb-1">
              <span className="flex items-center gap-1.5">
                {it.icon && <span>{it.icon}</span>}
                {it.label}
              </span>
              <span className="font-mono font-medium text-navy-700">
                {it.value.toLocaleString('es-EC')}
                {showPercent && <span className="text-slate-400 ml-1">({pct.toFixed(0)}%)</span>}
              </span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${pct}%`, backgroundColor: it.color ?? '#64748b' }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

interface VerticalBarChartProps {
  values: number[]
  labels?: string[]
  color?: string
  height?: number
  className?: string
}

export function VerticalBarChart({ values, labels, color = '#10b981', height = 140, className }: VerticalBarChartProps) {
  const max = Math.max(...values, 1)
  return (
    <div className={cn('relative', className)} style={{ height }}>
      <div className="absolute inset-0 flex items-end gap-1 pr-2 pb-5">
        {values.map((v, i) => {
          const h = (v / max) * 100
          return (
            <div key={i} className="flex-1 flex flex-col items-center justify-end gap-1">
              <div
                className="w-full rounded-t transition-all hover:opacity-80"
                style={{ height: `${h}%`, backgroundColor: color, opacity: 0.6 + (i / values.length) * 0.4 }}
                title={`${v}`}
              />
              {labels && (
                <span className="text-[9px] text-slate-500 truncate w-full text-center">
                  {labels[i]}
                </span>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

interface AreaLineChartProps {
  values: number[]
  color?: string
  height?: number
  className?: string
}

export function AreaLineChart({ values, color = '#10b981', height = 160, className }: AreaLineChartProps) {
  const max = Math.max(...values, 1)
  const min = Math.min(...values, 0)
  const range = max - min || 1
  const width = 400
  const stepX = width / Math.max(values.length - 1, 1)
  const pts = values.map((v, i) => [i * stepX, height - ((v - min) / range) * (height - 10) - 5])
  const linePath = pts.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ')
  const areaPath = `${linePath} L${width},${height} L0,${height} Z`
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className={cn('w-full', className)} preserveAspectRatio="none" style={{ height }}>
      <defs>
        <linearGradient id={`area-grad-${color.replace('#', '')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.3" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill={`url(#area-grad-${color.replace('#', '')})`} />
      <path d={linePath} stroke={color} strokeWidth="2" fill="none" />
      {pts.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r="3" fill={color} />
      ))}
    </svg>
  )
}

interface DonutChartProps {
  segments: { label: string; value: number; color: string }[]
  size?: number
  className?: string
  centerLabel?: string
  centerValue?: string
}

export function DonutChart({ segments, size = 160, className, centerLabel, centerValue }: DonutChartProps) {
  const total = segments.reduce((acc, s) => acc + s.value, 0)
  const r = size / 2 - 20
  const circ = 2 * Math.PI * r
  let offset = 0
  return (
    <div className={cn('relative inline-block', className)} style={{ width: size, height: size }}>
      <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-full -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#f1f5f9" strokeWidth="20" />
        {segments.map((s, i) => {
          const len = (s.value / total) * circ
          const el = (
            <circle
              key={i}
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={s.color}
              strokeWidth="20"
              strokeDasharray={`${len} ${circ}`}
              strokeDashoffset={-offset}
            />
          )
          offset += len
          return el
        })}
      </svg>
      {(centerLabel || centerValue) && (
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          {centerValue && (
            <div className="text-2xl font-display text-navy-700 leading-none">{centerValue}</div>
          )}
          {centerLabel && (
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{centerLabel}</div>
          )}
        </div>
      )}
    </div>
  )
}
